/**
 * API Retry Utility with Cold Start Handling
 * 
 * Handles Render.com free tier cold starts with user-friendly messaging.
 * Retries API calls for up to 60 seconds with engaging progress messages.
 */

/**
 * Engaging messages to display during cold start wait
 * Keeps users informed and patient while API wakes up
 */
export const COLD_START_MESSAGES = [
  { time: 0, message: "🔄 Waking up the server...", detail: "This may take a moment on first load" },
  { time: 10000, message: "⏳ Still warming up...", detail: "Free tier servers sleep when idle" },
  { time: 20000, message: "🌟 Almost ready...", detail: "Thanks for your patience!" },
  { time: 30000, message: "🚀 Final preparations...", detail: "Just a few more seconds" },
  { time: 45000, message: "⚡ Loading your data...", detail: "Server is now active" },
];

/**
 * Final failure message after all retries exhausted
 */
export const COLD_START_FAILURE = {
  message: "❌ Unable to connect to server",
  detail: "Please try again in a moment or contact support if this persists",
};

/**
 * Retry an API call with exponential backoff and user messaging
 * 
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} options - Retry configuration
 * @param {number} options.maxAttempts - Maximum retry attempts (default: 10)
 * @param {number} options.initialDelay - Initial delay in ms (default: 5000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 8000)
 * @param {Function} options.onProgress - Callback for progress updates (message, detail, attempt)
 * @param {Function} options.onError - Callback for final error
 * @returns {Promise} - Resolves with API response or rejects after all retries
 */
export async function retryWithColdStart(apiCall, options = {}) {
  const {
    maxAttempts = 10, // 10 attempts * ~5s avg = ~55s total (within 60s test timeout)
    initialDelay = 5000,
    maxDelay = 8000, // Reduced to ensure total time stays under 60s
    onProgress = () => {},
    onError = () => {},
  } = options;

  let lastError = null;
  let currentMessageIndex = 0;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // Update progress message based on elapsed time
      const elapsedTime = (attempt - 1) * initialDelay;
      while (
        currentMessageIndex < COLD_START_MESSAGES.length - 1 &&
        elapsedTime >= COLD_START_MESSAGES[currentMessageIndex + 1].time
      ) {
        currentMessageIndex++;
      }

      if (attempt > 1) {
        const currentMessage = COLD_START_MESSAGES[currentMessageIndex];
        onProgress(currentMessage.message, currentMessage.detail, attempt);
      }

      // Attempt the API call
      const response = await apiCall();
      
      // Success - clear any error state
      return response;

    } catch (error) {
      lastError = error;
      
      // If this is the last attempt, fail
      if (attempt === maxAttempts) {
        onError(COLD_START_FAILURE.message, COLD_START_FAILURE.detail);
        throw error;
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(initialDelay * Math.pow(1.5, attempt - 1), maxDelay);
      
      // Wait before next attempt
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // Should never reach here, but just in case
  throw lastError || new Error('Max retry attempts exceeded');
}

/**
 * Check if an error is likely a cold start (503, ECONNREFUSED, timeout)
 */
export function isColdStartError(error) {
  if (!error) return false;
  
  // HTTP 503 Service Unavailable
  if (error.response?.status === 503) return true;
  
  // Network errors (connection refused, timeout)
  if (error.code === 'ECONNREFUSED') return true;
  if (error.code === 'ETIMEDOUT') return true;
  if (error.message?.includes('timeout')) return true;
  if (error.message?.includes('Network Error')) return true;
  
  return false;
}

/**
 * Wrap an API call with automatic retry on cold start errors
 * 
 * @param {Function} apiCall - Async function that makes the API call
 * @param {Object} options - Retry configuration (same as retryWithColdStart)
 * @returns {Promise} - Resolves with API response or rejects
 */
export async function withColdStartRetry(apiCall, options = {}) {
  try {
    // First attempt without retry
    return await apiCall();
  } catch (error) {
    // Only retry if it's a cold start error
    if (isColdStartError(error)) {
      return await retryWithColdStart(apiCall, options);
    }
    // Re-throw non-cold-start errors immediately
    throw error;
  }
}
